import { useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import {
  BookOpenText,
  CalendarClock,
  ChevronsUpDown,
  CircleHelp,
  ClipboardCheck,
  Compass,
  FileDown,
  FileInput,
  FileText,
  FileUp,
  Fuel,
  Globe2,
  KeyRound,
  Languages,
  LayoutGrid,
  type LucideIcon,
  Map as MapIcon,
  MountainSnow,
  NotebookPen,
  ListTree,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Route,
  ScrollText,
  ShieldCheck,
  Search,
  Settings2,
  HardDrive,
  Sparkles,
  Upload,
  UserRound,
  Waypoints,
  Wrench,
} from "lucide-react";
import { Fragment, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { IaMenuItems, IaSwitch, useIaOffered } from "../ai/IaSwitch";
import { AccountButton } from "../auth/AccountMenu";
import { api } from "../api/client";
import type { Exercise, Zone } from "../api/types";
import { attentionSelector, designateSoon } from "../components/attention";
import { Spinner } from "../components/EmptyState";
import { GenerexMark, GenerexWordmark } from "../components/GenerexMark";
import { Menu, MenuItem, MenuLabel, MenuSeparator } from "../components/Menu";
import { motion, Motion, Presence, useClosing, usePresence } from "../components/presence";
import { ScrollFades, useScrollEdges } from "../components/scrollEdges";
import { Badge, IconButton, Kbd, SectionLabel, shortcut } from "../components/ui";
import { useDismiss } from "../components/useDismiss";
import { useMediaQuery } from "../components/useMediaQuery";
import { k, lang, t } from "../i18n/i18n";
import { LangIndicator, LangRailMenu, LangSwitch } from "../i18n/LangSwitch";
import { currentHelpScreen } from "../help/HelpButton";
import { useGuideDownload } from "../help/guide";
import { useHelp } from "../help/helpStore";
import { IdentityDialog, identityLabel } from "../licence/IdentityScreen";
import { LicenceDialog, useLicenceSummary } from "../licence/LicenceFooter";
import { ReadOnlySidebar } from "../licence/ReadOnlyLicence";
import { DossierSharingBar } from "../sharing/ReadOnlyBanner";
import { DossierExportMenu } from "./DossierExportMenu";
import { FeedbackLauncher } from "../feedback/FeedbackHost";
import { orderNumber, orderState } from "./dossierState";
import { SidebarStorage, storageMenuEntry, useStorageDialog } from "../licence/StorageGauge";
import { useTrialExport } from "../licence/trial";
import { SidebarVersion } from "./SidebarVersion";
import { useOpenZone, useStorage, ZONES_KEY } from "../map/zones/useZones";
import { keepsZone, recreateZoneAction, showZoneWait } from "../map/zones/zoneAttention";
import { zoneChip as chipOf, zoneReady } from "../map/zones/zoneState";
import { ZoneChipBadge, type ZoneChipTone, zoneChipText } from "../map/zones/ZoneChipBadge";
import { dossierZone, importDossierFile } from "./home/dossier";
import { ImportOpoDialog, useOpoImport } from "./home/ImportOpoDialog";
import { levelLabel } from "../missions";
import { useReferenceEnemy } from "../enemyDisplay";
import { useUi, type ViewKey } from "../state/ui";
import { useBriefingJob } from "../views/briefing/briefingJob";
import { useCountryJob } from "../views/country/countryJob";
import {
  declinable,
  declineAfterTerrain,
  GENERATE_TARGET,
  offerAfterImport,
  offerAfterTerrain,
  startAfterImport,
} from "../views/declination/declination";
import { Waiting } from "../components/Waiting";
import { useCoherenceReport } from "../views/coherence/CoherencePanel";
import { plural } from "../plural";

/** Infobulle de la pastille de la cohérence tactique. */
function coherenceTitle(blocking: number, review: number): string {
  return t("Cohérence tactique : {blocking}, {review}", {
    blocking: plural(blocking, t("point bloquant"), t("points bloquants")),
    review: plural(review, t("point à revoir"), t("points à revoir")),
  });
}

interface NavItem {
  key: ViewKey;
  label: string;
  icon: LucideIcon;
  /** Vue pas encore construite (plan de développement, docs/04). */
  upcoming?: boolean;
}

/** Barre latérale rangée dans l'ordre du travail (demande d'Alexis du 28/10/2026 : « Objets de la mission » retirés,
 *  « revois la disposition des autres éléments ») : préparer, rédiger le dossier, conduire l'exercice, documents. */
const PREPARATION: NavItem[] = [
  { key: "mission", label: k("Données initiales"), icon: MapIcon },
  { key: "manoeuvre", label: k("Manœuvre"), icon: Route },
  { key: "terrain", label: k("Analyse du terrain"), icon: MountainSnow },
  { key: "coa", label: k("Modes d'action"), icon: Waypoints },
  { key: "logistics", label: k("Logistique"), icon: Fuel },
  // Maintenance (DTO, CAPOPS) : indicateurs de nature différente du ravitaillement, vue à part sous la Logistique.
  { key: "maintenance", label: k("Maintenance"), icon: Wrench },
  // Cohérence tactique (docs/24) : pastille du nombre de points à revoir (bloquants en rouge).
  { key: "coherence", label: k("Cohérence tactique"), icon: ShieldCheck },
];

/** Conduite de l'exercice et retour d'expérience. */
const CONDUCT: NavItem[] = [
  { key: "timeline", label: k("DIREX"), icon: CalendarClock },
  { key: "evaluation", label: k("3A"), icon: ClipboardCheck },
  { key: "retex", label: k("RETEX"), icon: NotebookPen },
];

/** Dossier d'exercice (précisions d'Alexis du 29/09/2026) : la fiche pays (demande du 11/10/2026), la mise en
 *  ambiance (Partie I) et l'OPO validé forment le dossier remis aux joueurs, exporté en un seul PDF ; encadré
 *  au fond grisé dans « Documents ». */
const DOSSIER: { view: ViewKey; label: string; icon: LucideIcon }[] = [
  { view: "country", label: k("Fiche pays"), icon: Globe2 },
  { view: "briefing", label: k("Mise en ambiance"), icon: BookOpenText },
  // Corps d'armée seulement (docs/08 §25) : l'OPLAN du corps, juste avant l'OPO qui en est tiré.
  { view: "oplan", label: k("Plan d'opération (OPLAN)"), icon: ScrollText },
  // Libellé court : la pastille « Brouillon » ou « Validé » tient sur la ligne.
  { view: "order", label: k("Ordre d'opérations"), icon: FileText },
];

/** Autres documents du dossier : ordres reçus et subordonnés, exports vers les outils OTAN. */
const DOCUMENTS: NavItem[] = [
  { key: "received", label: k("Ordres reçus"), icon: FileInput },
  // Dossier de brigade ou de division seulement : les ordres de ses subordonnés (docs/20 §13).
  { key: "declination", label: k("Ordres subordonnés"), icon: ListTree },
  { key: "exports", label: k("Exports"), icon: FileUp },
];

/** Tablette (sous 1024 px, R16) : la barre est un rail de 60 px qui se déplie en surimpression. Son choix est
 *  mémorisé à part de celui de l'ordinateur (`sidebarCollapsed`), sur ce poste. */
export const TABLET_QUERY = "(max-width: 1023.98px)";
const TABLET_KEY = "generex:barre-tablette";

export function readTabletOpen(): boolean {
  try {
    return localStorage.getItem(TABLET_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeTabletOpen(open: boolean): void {
  try {
    if (open) localStorage.setItem(TABLET_KEY, "1");
    else localStorage.removeItem(TABLET_KEY);
  } catch {
    /* stockage bloqué : le choix vaut jusqu'au rechargement */
  }
}

/** Texte de la barre qui apparaît et disparaît en fondu quand la barre se déplie ou se replie (R11). */
function Fade({ show, className, children }: { show: boolean; className?: string; children: ReactNode }) {
  return <Presence show={show}>{show && <FadeSpan className={className}>{children}</FadeSpan>}</Presence>;
}

function FadeSpan({ className, children }: { className?: string; children: ReactNode }) {
  const closing = useClosing();
  return <span className={clsx(motion("fade", closing), className)}>{children}</span>;
}

/** Entrée de la barre qui apparaît ou disparaît selon le dossier (RETEX face à l'ennemi générique) : en fondu. */
function FadeBlock({ children }: { children: ReactNode }) {
  const closing = useClosing();
  return <div className={motion("fade", closing)}>{children}</div>;
}

/** Bloc qui cède sa place à un autre quand la barre se replie ou se déplie (R11) : il entre en fondu ; à la
 *  sortie, il quitte le flux (`exitClassName`, en position absolue) pour que son remplaçant prenne aussitôt sa
 *  place, et s'efface par-dessus sans faire sauter la barre. */
function Swap({
  show,
  className,
  exitClassName,
  title,
  children,
}: {
  show: boolean;
  className?: string;
  exitClassName: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <Presence show={show}>
      {show && (
        <SwapBody className={className} exitClassName={exitClassName} title={title}>
          {children}
        </SwapBody>
      )}
    </Presence>
  );
}

function SwapBody({ className, exitClassName, title, children }: { className?: string; exitClassName: string; title?: string; children: ReactNode }) {
  const closing = useClosing();
  return (
    <div title={title} aria-hidden={closing || undefined} className={clsx(motion("fade", closing), className, closing && clsx("pointer-events-none", exitClassName))}>
      {children}
    </div>
  );
}

/** Point d'état posé sur l'icône d'une entrée dans le rail (la pastille `trailing` y est masquée). */
const RAIL_DOT: Record<ZoneChipTone, string> = { run: "bg-run", danger: "bg-danger", neutral: "bg-ink-muted" };

/** Entrée de navigation (R4 : 28 px, espacées de 2 px). L'icône garde sa place quand la barre se replie : seul le
 *  libellé s'efface. `blocked` : préalable manquant, le clic désigne l'action attendue (R12). `dot` : état durable
 *  (préparation de la zone libre) montré par un point sur l'icône quand la barre est repliée en rail, son libellé
 *  dans l'infobulle. */
function NavButton({
  icon: Icon,
  label,
  active,
  collapsed,
  onClick,
  trailing,
  blocked,
  onBlocked,
  blockedTitle,
  inset = false,
  dot,
}: {
  icon: LucideIcon;
  label: string;
  active?: boolean;
  collapsed: boolean;
  onClick?: () => void;
  trailing?: ReactNode;
  blocked?: boolean;
  onBlocked?: () => void;
  /** Infobulle d'une entrée bloquée (licence d'essai : « Export impossible avec une licence d'essai. »). */
  blockedTitle?: string;
  /** Posé sur un fond grisé (dossier d'exercice) : sélection et survol en blanc. */
  inset?: boolean;
  dot?: { tone: keyof typeof RAIL_DOT; label: string };
}) {
  return (
    <button
      type="button"
      onClick={blocked ? onBlocked : onClick}
      aria-disabled={blocked || undefined}
      aria-current={active ? "page" : undefined}
      title={blocked && blockedTitle ? (collapsed ? `${label} · ${blockedTitle}` : blockedTitle) : collapsed ? (dot ? `${label} · ${dot.label}` : label) : undefined}
      className={clsx(
        // Marge de défilement : l'entrée amenée dans le champ s'arrête au-dessus du fondu du bord.
        "flex h-7 w-full scroll-my-10 items-center gap-2.5 rounded-lg text-ui transition-colors",
        collapsed && inset ? "justify-center px-0" : "px-3",
        active
          ? clsx("font-medium text-ink", inset ? "bg-surface shadow-sm" : "bg-selected")
          : clsx("text-ink", !blocked && (inset ? "hover:bg-surface/70" : "hover:bg-hover")),
        blocked && "opacity-40",
      )}
    >
      <span className="relative inline-flex shrink-0">
        <Icon size={16} className="shrink-0 text-ink" aria-hidden />
        <Fade
          show={collapsed && Boolean(dot)}
          className={clsx("absolute -top-0.5 -right-1 size-2 rounded-full ring-2 ring-surface", dot && RAIL_DOT[dot.tone])}
        >
          {collapsed && dot && <span className="sr-only">{dot.label}</span>}
        </Fade>
      </span>
      <Fade show={!collapsed} className="min-w-0 flex-1 truncate text-left">
        {label}
      </Fade>
      <Fade show={!collapsed && Boolean(trailing)} className="inline-flex shrink-0">
        {trailing}
      </Fade>
    </button>
  );
}

/** Largeur commune des sélecteurs du pied (langue, modèle d'IA), segments à leur largeur naturelle : « Advanced »
 *  est plus long que « Avancé », et « AI model » plus court que « Modèle d'IA » à gauche. L'interface se recharge à
 *  chaque changement de langue. */
const footerSwitch = () => (lang() === "en" ? "w-[124px] shrink-0" : "w-[108px] shrink-0");

/** Titre de rubrique, remplacé par un filet dans le rail. */
function NavSection({ collapsed, children }: { collapsed: boolean; children: ReactNode }) {
  return (
    <div className="relative">
      <Swap show={!collapsed} exitClassName="absolute inset-x-0 top-0">
        <SectionLabel className="px-1">{children}</SectionLabel>
      </Swap>
      <Swap show={collapsed} exitClassName="absolute inset-x-0 top-0">
        <div className="my-3 border-t border-line" />
      </Swap>
    </div>
  );
}

/** Menu du pied de la barre : licence du poste, aide et glossaire, prise en main ; dans le rail, aussi la langue
 *  de l'interface et le modèle d'IA (visibles en clair dans le pied de la barre dépliée). */
function ShellMenu({ collapsed, onLicence, onSeat }: { collapsed: boolean; onLicence: () => void; onSeat: () => void }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const openHelp = useHelp((s) => s.openHelp);
  const openOnboarding = useHelp((s) => s.openOnboarding);
  const guide = useGuideDownload();
  const iaOffered = useIaOffered();
  const licence = useLicenceSummary();
  const { data: storage } = useStorage(open);
  const storageEntry = storageMenuEntry(storage);
  const showStorage = useStorageDialog((s) => s.show);
  return (
    <Menu
      open={open}
      onClose={close}
      portal
      side={collapsed ? "right" : "top"}
      align={collapsed ? "end" : "start"}
      width={collapsed ? 264 : 236}
      ariaLabel={t("Réglages, licence et aide")}
      wrapperClassName={collapsed ? undefined : "flex w-full"}
      trigger={
        collapsed ? (
          <IconButton size="sm" onClick={() => setOpen((o) => !o)} title={t("Réglages, licence et aide")} aria-label={t("Réglages, licence et aide")}>
            <Settings2 size={16} />
          </IconButton>
        ) : (
          // Barre dépliée : ligne libellée « Réglages », alignée sur les entrées des vues (icône et texte).
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            title={t("Réglages, licence et aide")}
            aria-expanded={open}
            className={clsx(
              "flex h-7 w-full items-center gap-2.5 rounded-lg px-3 text-ui text-ink transition-colors hover:bg-hover",
              open && "bg-selected",
            )}
          >
            <Settings2 size={16} className="shrink-0 text-ink" aria-hidden />
            <span className="min-w-0 flex-1 truncate text-left">{t("Réglages")}</span>
          </button>
        )
      }
    >
      {collapsed && (
        <>
          {/* Entrées à choix exclusif plutôt que des Segmented : un role="menu" ne parcourt que ses entrées au
              clavier (flèches), et un radiogroup n'y a pas sa place (ARIA). La langue a son propre bouton dans le
              rail (son drapeau, LangRailMenu). */}
          <IaMenuItems />
          {iaOffered && <MenuSeparator />}
        </>
      )}
      <MenuItem icon={KeyRound} label={licence.title} hint={<Waiting>{licence.detail}</Waiting>} onSelect={onLicence} />
      {/* Ce poste : la personne qui l'utilise (administration des licences), sauf en mode comptes (le compte connecté). */}
      {seatShown(licence.status) && (
        <MenuItem icon={UserRound} label={t("Ce poste")} hint={identityLabel(licence.status) ?? t("utilisateur à indiquer")} onSelect={onSeat} />
      )}
      {/* Espace de stockage de la licence : « Espace utilisé : x sur y » (décision d'Alexis du 02/10/2026). */}
      <MenuItem icon={HardDrive} label={storageEntry.label} hint={<Waiting>{storageEntry.hint}</Waiting>} onSelect={() => showStorage()} />
      <MenuItem
        icon={CircleHelp}
        label={t("Aide et glossaire")}
        trailing={<Kbd hideOnTouch>?</Kbd>}
        onSelect={() => openHelp({ screen: currentHelpScreen() })}
      />
      <MenuItem icon={Compass} label={t("Prise en main")} onSelect={openOnboarding} />
      <MenuItem icon={FileDown} label={t("Guide de prise en main (PDF)")} onSelect={guide.download} />
      {/* Retours des utilisateurs (registre F39) : un seul chemin, le bouton « Retours » (décision d'Alexis du
          21/10/2026), plus d'entrées ici. */}
    </Menu>
  );
}

/** « Ce poste » dans Réglages : poste ouvert sous contrôle de licence, hors mode comptes. */
function seatShown(status: ReturnType<typeof useLicenceSummary>["status"]): boolean {
  return Boolean(status?.enforced && status.licensed && status.accounts?.mode !== "accounts");
}

export function Sidebar({ onSearch }: { onSearch: () => void }) {
  const desktopCollapsed = useUi((s) => s.sidebarCollapsed);
  const toggleDesktop = useUi((s) => s.toggleSidebar);
  // Tablette en portrait (R16) : rail par défaut, dépliage en surimpression au-dessus de la carte.
  const tablet = useMediaQuery(TABLET_QUERY);
  const [tabletOpen, setTabletOpenState] = useState(readTabletOpen);
  const setTabletOpen = useCallback((next: boolean) => {
    setTabletOpenState(next);
    writeTabletOpen(next);
  }, []);
  const overlay = tablet && tabletOpen;
  const collapsed = tablet ? !tabletOpen : desktopCollapsed;
  const toggle = () => (tablet ? setTabletOpen(!tabletOpen) : toggleDesktop());
  // Pastille durable de la zone libre du dossier ouvert sur l'entrée « Manœuvre » (docs/14 §2.7.5) : en file, en
  // préparation (pourcentage), en échec.
  const { zone: openZone } = useOpenZone();
  const chip = chipOf(openZone);
  const chipText = chip ? zoneChipText(chip) : null;
  const zoneDot = chipText ? { tone: chipText.tone, label: chipText.title } : undefined;
  const zoneChip = chip ? <ZoneChipBadge chip={chip} /> : undefined;
  const closeOverlay = useCallback(() => setTabletOpen(false), [setTabletOpen]);
  // Échap ferme la surimpression (pile partagée) ; le voile la ferme au toucher.
  useDismiss(overlay, closeOverlay);
  // Plan relevé le temps de la sortie aussi : la barre qui se replie passe encore au-dessus des vues.
  const overlayLayer = usePresence(overlay).mounted;

  const view = useUi((s) => s.view);
  const setView = useUi((s) => s.setView);
  const showObjects = useUi((s) => s.showObjects);
  const referenceEnemy = useReferenceEnemy();
  const trialExport = useTrialExport();
  const exerciseId = useUi((s) => s.exerciseId);
  // Cohérence tactique : points ouverts (bloquants et à revoir) du dossier enregistré, pastille et point du rail.
  const coherence = useCoherenceReport(Boolean(exerciseId)).data;
  const coherenceOpen = coherence && !coherence.incomplete ? coherence.counts.blocking + coherence.counts.review : 0;
  const coherenceTone = coherence?.counts.blocking ? "danger" : "warn";
  const coherenceChip =
    coherenceOpen > 0 ? (
      <Badge tone={coherenceTone} title={coherenceTitle(coherence!.counts.blocking, coherence!.counts.review)}>
        {coherenceOpen}
      </Badge>
    ) : undefined;
  const coherenceDot =
    coherence?.counts.blocking ? { tone: "danger" as const, label: coherenceTitle(coherence.counts.blocking, coherence.counts.review) } : undefined;
  const briefingRunning = useBriefingJob((s) => Boolean(s.job?.running && s.job.exerciseId === exerciseId));
  const countryRunning = useCountryJob((s) => Boolean(s.job?.running && s.job.exerciseId === exerciseId));
  const setExercise = useUi((s) => s.setExercise);
  const notify = useUi((s) => s.notify);
  const openWelcome = useUi((s) => s.openWelcome);
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const closePicker = useCallback(() => setPickerOpen(false), []);
  const [licenceOpen, setLicenceOpen] = useState(false);
  const [seatOpen, setSeatOpen] = useState(false);
  const { status: licenceStatus } = useLicenceSummary();
  const importInput = useRef<HTMLInputElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const edges = useScrollEdges(navRef, collapsed);
  const iaOffered = useIaOffered();
  const openHelp = useHelp((s) => s.openHelp);
  // La vue ouverte ailleurs (recherche ⌘K, bouton d'une fiche) reste visible dans la liste.
  useEffect(() => {
    navRef.current?.querySelector('[aria-current="page"]')?.scrollIntoView({ block: "nearest" });
  }, [view]);

  /** Choix d'une entrée : la surimpression de la tablette se referme. */
  const chosen = () => {
    if (overlay) closeOverlay();
  };
  const go = (next: ViewKey) => {
    setView(next);
    chosen();
  };

  const exercisesQuery = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const exercises = exercisesQuery.data ?? [];
  const mapOpo = useUi((s) => s.opo);
  const current = exercises.find((e) => e.id === exerciseId);
  // OPO du conteneur « Dossier d'exercice » : brouillon ou validé (celui de la rotation affiché en CENTAC).
  const orderStatus = exerciseId ? orderState(current, orderNumber(current, mapOpo)) : "none";

  // Dossier rouvert à la relance (mémorisé sur ce poste) : oublié s'il a été supprimé entre-temps.
  // Seulement sur une liste à jour : un dossier qui vient d'être créé n'y est peut-être pas encore.
  const listSettled = exercisesQuery.isSuccess && !exercisesQuery.isFetching;
  useEffect(() => {
    if (listSettled && exerciseId && !exercises.some((e) => e.id === exerciseId)) {
      setExercise(null, null);
    }
  }, [listSettled, exercises, exerciseId, setExercise]);

  const importDossier = async (file: File) => {
    try {
      const ex = await importDossierFile(file, queryClient);
      setExercise(ex.id, ex.map_slug);
      go("mission");
      notify({
        text: t("Dossier importé : {name}", { name: ex.name }),
        tone: "ok",
        action: recreateZoneAction(keepsZone(ex.parameters, ex.notices)),
      });
    } catch (err) {
      notify({ text: t("Import impossible"), detail: err instanceof Error ? err.message : undefined, tone: "error" });
    }
  };

  // OPO importé (docs/20-import-opo.md) : le dossier recréé s'ouvre sur sa carte (sur l'attente de son terrain libre
  // tant qu'il se prépare), l'OPO dans ses ordres reçus.
  const opoOpen = useOpoImport((s) => s.openIn === "app");
  const openOpo = useOpoImport((s) => s.open);
  const closeOpo = useOpoImport((s) => s.close);
  const opoCreated = async (ex: Exercise) => {
    void queryClient.invalidateQueries({ queryKey: ["exercises"] });
    if (ex.map_slug?.startsWith("zone-")) await queryClient.invalidateQueries({ queryKey: ZONES_KEY }).catch(() => undefined);
    const decline = useOpoImport.getState().decline;
    closeOpo();
    setExercise(ex.id, ex.map_slug);
    const zone = dossierZone(queryClient.getQueryData<Zone[]>(ZONES_KEY), ex.map_slug);
    // Brigade ou division : les ordres subordonnés se génèrent directement, l'action est désignée (docs/20 §13).
    const ready = !zone || zoneReady(zone);
    // Déclinaison choisie à l'import : lancée aussitôt quand le terrain est prêt ; sinon le dossier s'ouvre sur sa
    // carte, et « Générer les ordres » reste offert dans la vue des ordres subordonnés.
    const offer = decline && offerAfterImport(ex.parameters.level, ex.map_slug, ready);
    const started = offer && (await startAfterImport(ex.id, lang()));
    // Terrain en préparation : l'avis « Terrain prêt » proposera la génération (intention retenue pour ce dossier).
    const later = decline && offerAfterTerrain(ex.parameters.level, ex.map_slug, ready);
    if (later) declineAfterTerrain(ex.id);
    if (offer) {
      go("declination");
      if (!started) designateSoon(attentionSelector(GENERATE_TARGET), "auto", 900);
    } else if (zone && !zoneReady(zone)) showZoneWait(zone);
    else showObjects("all");
    notify({
      text: t("Dossier créé : {name}", { name: ex.name }),
      detail: offer
        ? started
          ? t("L'OPO importé est rangé dans ses ordres reçus ; les ordres des échelons inférieurs sont en cours de rédaction.")
          : t("L'OPO importé est rangé dans ses ordres reçus. Prochaine étape : générer les ordres des subordonnés.")
        : later
          ? t("L'OPO importé est rangé dans ses ordres reçus. Prochaine étape, dès que le terrain sera prêt : générer les ordres des subordonnés.")
          : t("L'OPO importé est rangé dans ses ordres reçus."),
      tone: "ok",
      action: recreateZoneAction(keepsZone(ex.parameters, ex.notices)),
    });
  };

  // OPO sans données Generex (étape 2 à venir) : rangé dans les ordres reçus du dossier ouvert, qui s'ouvrent.
  const opoReceived = current
    ? {
        name: current.name,
        file: async (file: File) => {
          await api.uploadReceivedOrder(current.id, file);
          closeOpo();
          void queryClient.invalidateQueries({ queryKey: ["received", current.id], exact: true });
          go("received");
          notify({ text: t("OPO rangé dans les ordres reçus"), detail: current.name, tone: "ok" });
        },
      }
    : null;

  /** Entrée de la conduite ou des documents. RETEX : inactif face à l'ennemi générique (force non française, registre
   *  B39). Ordres subordonnés : dossier de brigade ou de division. L'entrée s'efface en fondu, et revient de même. */
  const entry = (item: NavItem) => {
    const button = (
      <NavButton
        key={item.key}
        icon={item.icon}
        label={t(item.label)}
        collapsed={collapsed}
        active={view === item.key}
        onClick={() => go(item.key)}
        // Licence d'essai (registre F44) : « Exports » grisé, l'appui le dit (avis), le survol aussi.
        {...(item.key === "exports" && trialExport.blocked
          ? { blocked: true, onBlocked: trialExport.onBlocked, blockedTitle: trialExport.title }
          : {})}
      />
    );
    const shown =
      item.key === "retex" ? referenceEnemy : item.key === "declination" ? declinable(current?.parameters.level) : null;
    if (shown === null) return button;
    return (
      <Presence key={item.key} show={shown}>
        {shown && <FadeBlock>{button}</FadeBlock>}
      </Presence>
    );
  };

  const searchKey = shortcut("K");

  return (
    <div
      className={clsx(
        "relative h-full shrink-0 transition-[width] duration-(--gx-enter) ease-(--gx-ease-out) motion-reduce:transition-none",
        overlayLayer ? "z-40" : "z-20",
        tablet || collapsed ? "w-[60px]" : "w-[260px]",
      )}
    >
      {/* Voile de la surimpression (tablette) : au toucher, la barre se replie. */}
      <Presence show={overlay}>
        {overlay && <Motion origin="fade" aria-hidden onClick={closeOverlay} className="absolute inset-y-0 left-full w-screen bg-ink/20" />}
      </Presence>
      <aside
        className={clsx(
          "absolute inset-y-0 left-0 z-10 flex flex-col overflow-hidden border-r border-line bg-surface",
          "transition-[width,box-shadow] duration-(--gx-enter) ease-(--gx-ease-out) motion-reduce:transition-none",
          collapsed ? "w-[60px]" : "w-[260px]",
          overlay && "shadow-float",
        )}
      >
        <div className={clsx("relative flex h-14 shrink-0 items-center", collapsed ? "justify-center" : "justify-between px-3.5")}>
          {/* L'emblème ramène à l'accueil (dossiers), comme le logo de la plupart des applications. */}
          <button
            type="button"
            onClick={() => {
              openWelcome();
              chosen();
            }}
            title={t("Accueil : tous les dossiers")}
            className="-mx-1 flex items-center gap-2.5 rounded-lg px-1 py-1 transition-colors hover:bg-hover"
          >
            <GenerexMark size={26} />
            <Fade show={!collapsed} className="inline-flex">
              <GenerexWordmark />
            </Fade>
          </button>
          {/* Langue active (demandes d'Alexis du 24 et du 26/10/2026) : à droite du nom, après un séparateur vertical,
              un drapeau un peu plus grand ; marges de part et d'autre de la ligne (rien ne la touche). */}
          <Fade show={!collapsed} className="mr-auto ml-3 inline-flex items-center gap-3">
            <span aria-hidden className="h-5 w-px bg-line-strong" />
            <LangIndicator size={20} />
          </Fade>
          {/* À la sortie, le bouton garde sa place de la barre dépliée (bord droit, 260 − 14 − 28 px) : la barre qui
              se replie le masque sans qu'il passe sur l'emblème. */}
          <Swap show={!collapsed} exitClassName="absolute top-3.5 left-[218px]">
            <IconButton size="sm" onClick={toggle} title={t("Replier")} aria-label={t("Replier")} aria-expanded>
              <PanelLeftClose size={16} />
            </IconButton>
          </Swap>
        </div>

        <Swap show={collapsed} className="mb-2 flex justify-center" exitClassName="absolute top-14 left-0 w-[60px]">
          <IconButton size="sm" onClick={toggle} title={t("Déplier")} aria-label={t("Déplier")} aria-expanded={false}>
            <PanelLeftOpen size={16} />
          </IconButton>
        </Swap>

        {/* Espace de travail : le dossier d'exercice courant. */}
        <div className="shrink-0 px-2.5">
          <Menu
            open={pickerOpen}
            onClose={closePicker}
            portal
            side={collapsed ? "right" : "bottom"}
            width={236}
            ariaLabel={t("Dossiers")}
            wrapperClassName="flex w-full"
            trigger={
              <button
                type="button"
                onClick={() => setPickerOpen((o) => !o)}
                title={collapsed ? (current?.name ?? t("Aucun dossier")) : undefined}
                className={clsx(
                  "flex h-9 w-full items-center gap-2 rounded-lg transition-colors hover:bg-hover",
                  collapsed ? "justify-center" : "px-2",
                )}
              >
                <span className={clsx("size-2 shrink-0 rounded-full", current ? "bg-ok" : "bg-line-strong")} />
                <Fade show={!collapsed} className="min-w-0 flex-1 truncate text-left text-ui font-semibold text-ink">
                  {current?.name ?? t("Aucun dossier")}
                </Fade>
                <Fade show={!collapsed} className="inline-flex shrink-0 text-ink-muted">
                  <ChevronsUpDown size={14} />
                </Fade>
              </button>
            }
          >
            <MenuLabel>{t("Dossiers")}</MenuLabel>
            {exercises.length === 0 && <p className="px-2 py-1.5 text-ui text-ink-muted">{t("Aucun dossier.")}</p>}
            {exercises.map((ex) => (
              <MenuItem
                key={ex.id}
                label={ex.name}
                selected={ex.id === exerciseId}
                trailing={ex.parameters.level ? <Badge>{levelLabel(ex.parameters.level, ex.level_label)}</Badge> : undefined}
                onSelect={() => {
                  setExercise(ex.id, ex.map_slug);
                  go("mission");
                }}
              />
            ))}
            <MenuSeparator />
            <MenuItem
              icon={LayoutGrid}
              label={t("Tous les dossiers…")}
              onSelect={() => {
                openWelcome();
                chosen();
              }}
            />
            <MenuItem
              icon={Plus}
              label={t("Nouveau dossier")}
              onSelect={() => {
                setExercise(null, null);
                go("mission");
              }}
            />
            <MenuItem
              icon={Upload}
              label={t("Importer un dossier…")}
              title={t("Recharger un dossier téléchargé (fichier .generex)")}
              onSelect={() => importInput.current?.click()}
            />
            <MenuItem
              icon={FileInput}
              label={t("Importer un OPO…")}
              title={t("OPO de Generex ou ordre d'état-major (PDF, Word, LibreOffice, RTF, texte) : le dossier est recréé, carte peuplée")}
              onSelect={() => openOpo("app")}
            />
          </Menu>
          {/* Comptes nominatifs (docs/17 §8.3) : partage et lecture seule du dossier ouvert ; rien en mode hérité. */}
          <DossierSharingBar collapsed={collapsed} />
          <input
            ref={importInput}
            type="file"
            accept=".generex,application/json,.json"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) void importDossier(file);
            }}
          />
        </div>

        <div className="shrink-0 px-2.5 pt-1.5">
          <button
            type="button"
            onClick={() => {
              chosen();
              onSearch();
            }}
            title={collapsed ? t("Rechercher ({touche})", { touche: searchKey }) : undefined}
            aria-label={t("Rechercher ({touche})", { touche: searchKey })}
            className={clsx(
              "flex h-8 w-full items-center gap-2 rounded-lg border border-line-strong bg-surface text-ui text-ink-muted transition-colors hover:bg-hover",
              collapsed ? "justify-center" : "px-2.5",
            )}
          >
            <Search size={14} className="shrink-0" aria-hidden />
            <Fade show={!collapsed} className="min-w-0 flex-1 truncate text-left">
              {t("Rechercher…")}
            </Fade>
            <Fade show={!collapsed} className="inline-flex shrink-0">
              <Kbd hideOnTouch>{searchKey}</Kbd>
            </Fade>
          </button>
        </div>

        {/* Liste des vues à sa hauteur naturelle (demande d'Alexis du 28/10/2026) : le pied (retours, langue, modèle
            d'IA, réglages) la suit juste en dessous au lieu de rester collé au bas de l'écran ; la liste défile seule
            quand la fenêtre est trop basse. */}
        <div className="relative flex min-h-0 flex-initial flex-col">
          <nav ref={navRef} className="min-h-0 overflow-y-auto px-2.5 pb-1">
            <NavSection collapsed={collapsed}>{t("Préparation")}</NavSection>
            <div className="space-y-0.5">
              {/* « Manœuvre » toujours présente : en CENTAC, son panneau explique que la rotation suit les limites
                  du camp de Mailly (docs/14 §2.11). */}
              {PREPARATION.map((item) => (
                <NavButton
                  key={item.key}
                  icon={item.icon}
                  label={t(item.label)}
                  collapsed={collapsed}
                  active={view === item.key}
                  onClick={() => go(item.key)}
                  trailing={
                    item.key === "manoeuvre" ? (
                      zoneChip
                    ) : item.key === "coherence" ? (
                      <Presence show={Boolean(coherenceChip)}>{coherenceChip && <Motion origin="fade">{coherenceChip}</Motion>}</Presence>
                    ) : undefined
                  }
                  dot={item.key === "manoeuvre" ? zoneDot : item.key === "coherence" ? coherenceDot : undefined}
                />
              ))}
            </div>

            <NavSection collapsed={collapsed}>{t("Rédaction")}</NavSection>
            <div>
              <div
                className={clsx("relative rounded-xl border border-line bg-canvas", collapsed ? "space-y-0.5 p-0.5" : "p-1")}
                title={collapsed ? t("Dossier d'exercice") : undefined}
              >
                <Swap
                  show={!collapsed}
                  className="flex h-7 items-center justify-between pr-0.5 pl-2"
                  exitClassName="absolute inset-x-1 top-1 bg-canvas"
                >
                  <span className="truncate text-meta font-semibold text-ink-soft">{t("Dossier d'exercice")}</span>
                  <DossierExportMenu />
                </Swap>
                <div className="space-y-0.5">
                  {DOSSIER.map((item) => {
                    const button = (
                    <NavButton
                      key={item.label}
                      icon={item.icon}
                      label={t(item.label)}
                      collapsed={collapsed}
                      inset
                      active={view === item.view}
                      onClick={() => go(item.view)}
                      trailing={
                        item.view === "briefing" && briefingRunning ? (
                          <span title={t("L'IA rédige la mise en ambiance")} className="inline-flex">
                            <Spinner tone="run" label={t("L'IA rédige la mise en ambiance")} />
                          </span>
                        ) : item.view === "country" && countryRunning ? (
                          <span title={t("L'IA rédige la fiche pays")} className="inline-flex">
                            <Spinner tone="run" label={t("L'IA rédige la fiche pays")} />
                          </span>
                        ) : item.view === "oplan" && current?.orders?.oplan ? (
                          // État de l'OPLAN du corps (docs/08 §25), discret comme celui de l'OPO.
                          <Badge size="sm" tone={current.orders.oplan === "finalized" ? "ok" : "neutral"}>
                            {current.orders.oplan === "finalized" ? t("Validé") : t("Brouillon")}
                          </Badge>
                        ) : item.view === "order" && orderStatus !== "none" ? (
                          // État de l'OPO affiché, discret : le dossier complet attend sa validation.
                          <Badge size="sm" tone={orderStatus === "approved" ? "ok" : "neutral"}>
                            {orderStatus === "approved" ? t("Validé") : t("Brouillon")}
                          </Badge>
                        ) : undefined
                      }
                    />
                    );
                    // OPLAN : dossier de corps d'armée seulement ; l'entrée apparaît et s'efface en fondu.
                    if (item.view !== "oplan") return <Fragment key={item.label}>{button}</Fragment>;
                    const shown = current?.parameters.level === "CORPS";
                    return (
                      <Presence key={item.label} show={shown}>
                        {shown && <FadeBlock>{button}</FadeBlock>}
                      </Presence>
                    );
                  })}
                </div>
              </div>
            </div>

            <NavSection collapsed={collapsed}>{t("Conduite")}</NavSection>
            <div className="space-y-0.5">{CONDUCT.map(entry)}</div>

            <NavSection collapsed={collapsed}>{t("Documents")}</NavSection>
            <div className="space-y-0.5">{DOCUMENTS.map(entry)}</div>
          </nav>
          {/* Entrées cachées au-dessus ou au-dessous : fondu du bord, qui apparaît et disparaît en douceur. */}
          <ScrollFades edges={edges} />
        </div>

        {/* Licence expirée : bandeau de la lecture seule (icône dans le rail), au-dessus du pied. */}
        <ReadOnlySidebar collapsed={collapsed} />
        {/* Comptes nominatifs (docs/17 §8.3) : menu du compte au-dessus de la licence ; absent en mode hérité. */}
        <AccountButton collapsed={collapsed} />
        {/* Pied : bouton « Retours » (registre F39), puis une rangée de 41 px : langue de l'interface (FR · EN) et modèle d'IA (Rapide · Avancé, registre
            E26) toujours en clair dans la barre dépliée ; licence, aide et prise en main dans le menu des réglages.
            Dans le rail, le menu reprend aussi la langue et le modèle. */}
        {/* Rail : les retours et l'aide ont leur place, empilés au-dessus du menu (la barre dépliée n'a pas la largeur
            pour l'aide). */}
        <div className="relative shrink-0">
          <Swap
            show={collapsed}
            className="flex shrink-0 flex-col items-center gap-0.5 border-t border-line py-1.5"
            exitClassName="absolute top-0 left-0 w-[60px] bg-surface"
          >
            {/* Retours des utilisateurs (registre F39) : toujours visible, pastille des réponses à lire. */}
            <FeedbackLauncher place="rail" />
            <IconButton
              size="sm"
              onClick={() => openHelp({ screen: currentHelpScreen() })}
              title={t("Aide et glossaire (?)")}
              aria-label={t("Aide et glossaire")}
            >
              <CircleHelp size={16} />
            </IconButton>
            <LangRailMenu />
            <ShellMenu collapsed onLicence={() => setLicenceOpen(true)} onSeat={() => setSeatOpen(true)} />
          </Swap>
          <Swap
            show={!collapsed}
            className="flex shrink-0 flex-col border-t border-line"
            exitClassName="absolute top-0 left-0 w-[260px] bg-surface"
          >
            {/* Pied de la barre dépliée (demande d'Alexis du 28/10/2026) : une ligne par réglage, icônes et libellés
                alignés sur les entrées des vues (10 px de marge de la liste + 12 px de l'entrée) : retours, langue de
                l'interface, modèle d'IA (Rapide · Avancé, registre E26), puis « Réglages » (licence, stockage, aide,
                prise en main). Les choix sont alignés à droite, au bord des pastilles des entrées. */}
            <div className="px-2.5 py-1.5">
              {/* Retours des utilisateurs (registre F39) : toujours visible. */}
              <FeedbackLauncher place="sidebar" />
              {/* Langue et modèle d'IA : lignes un peu plus hautes que leur sélecteur (4 px d'écart entre les deux) et
                  sélecteurs de même largeur, segments égaux (demande d'Alexis du 28/10/2026). */}
              <div className="flex h-8 items-center gap-2.5 px-3 text-ui text-ink">
                <Languages size={16} className="shrink-0 text-ink" aria-hidden />
                <span className="min-w-0 flex-1 truncate">{t("Langue")}</span>
                <LangSwitch fill="natural" className={footerSwitch()} />
              </div>
              {iaOffered && (
                <div className="flex h-8 items-center gap-2.5 px-3 text-ui text-ink">
                  <Sparkles size={16} className="shrink-0 text-ink" aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{t("Modèle d'IA")}</span>
                  <IaSwitch fill="natural" className={footerSwitch()} />
                </div>
              )}
              <ShellMenu collapsed={false} onLicence={() => setLicenceOpen(true)} onSeat={() => setSeatOpen(true)} />
            </div>
            {/* Espace de stockage de la licence et boutons pour libérer de la place, sous les réglages. */}
            <div className="border-t border-line px-2.5">
              <SidebarStorage />
              {/* Numéro de version en grisé ; nouveautés au survol. */}
              <SidebarVersion />
            </div>
          </Swap>
        </div>
      </aside>
      <LicenceDialog open={licenceOpen} onClose={() => setLicenceOpen(false)} portal />
      {createPortal(
        <Presence show={seatOpen && Boolean(licenceStatus)}>
          {seatOpen && licenceStatus && <IdentityDialog status={licenceStatus} onClose={() => setSeatOpen(false)} />}
        </Presence>,
        document.body,
      )}
      {createPortal(
        <Presence show={opoOpen}>
          {opoOpen && (
            <ImportOpoDialog
              onClose={closeOpo}
              onCreated={opoCreated}
              onDossierFile={(file) => {
                closeOpo();
                void importDossier(file);
              }}
              received={opoReceived}
            />
          )}
        </Presence>,
        document.body,
      )}
    </div>
  );
}
